<template>
  <div
    class="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col gap-2 hover:shadow-md transition-shadow shadow-sm"
    :class="{ 'cursor-pointer': clickable }"
    @click="clickable ? $emit('click') : undefined"
  >
    <div class="flex items-center justify-between">
      <span class="text-sm text-gray-500">{{ title || label }}</span>
      <span
        class="w-9 h-9 rounded-xl flex items-center justify-center"
        :class="iconBgClass"
      >
        <i :class="['pi', icon, 'text-white text-sm']" />
      </span>
    </div>
    <div class="flex items-end justify-between gap-2">
      <span class="text-2xl font-bold text-gray-900">{{ displayValue }}</span>
      <span
        v-if="deltaPercent !== null"
        class="text-[11px] font-semibold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 mb-1"
        :class="deltaClasses"
        :title="'לעומת התקופה הקודמת'"
      >
        <i :class="['pi', deltaPercent > 0 ? 'pi-arrow-up' : deltaPercent < 0 ? 'pi-arrow-down' : 'pi-minus', '!text-[8px]']" />
        {{ Math.abs(deltaPercent) }}%
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{
  label?: string;
  title?: string;
  value: number | string;
  icon: string;
  color?: string;
  clickable?: boolean;
  /** Value of the previous period; renders a % change badge when provided */
  previousValue?: number | null;
}>();

defineEmits<{ (e: 'click'): void }>();

const displayValue = computed(() => {
  if (typeof props.value === 'string') return props.value;
  return props.value.toLocaleString('he-IL');
});

const deltaPercent = computed<number | null>(() => {
  if (props.previousValue === undefined || props.previousValue === null) return null;
  if (typeof props.value !== 'number') return null;
  if (props.previousValue === 0) return props.value > 0 ? 100 : null;
  return Math.round(((props.value - props.previousValue) / props.previousValue) * 100);
});

const deltaClasses = computed(() => {
  if (deltaPercent.value === null || deltaPercent.value === 0) return 'bg-gray-100 text-gray-500';
  return deltaPercent.value > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500';
});

const iconBgClass = computed(() => {
  const colors: Record<string, string> = {
    purple: 'bg-gradient-to-br from-purple-500 to-indigo-600',
    green: 'bg-gradient-to-br from-emerald-500 to-green-600',
    blue: 'bg-gradient-to-br from-blue-500 to-cyan-600',
    orange: 'bg-gradient-to-br from-orange-500 to-amber-600',
    amber: 'bg-gradient-to-br from-amber-500 to-yellow-600',
    red: 'bg-gradient-to-br from-red-500 to-rose-600',
    teal: 'bg-gradient-to-br from-teal-500 to-cyan-600',
    gray: 'bg-gradient-to-br from-gray-400 to-gray-500',
  };
  return colors[props.color || 'purple'];
});
</script>
